import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { formatDate, formatDateTime } from '@utils/dateUtils'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Divider,
  Grid,
  CircularProgress,
  Chip,
} from '@mui/material'
import { Print, ArrowBack } from '@mui/icons-material'
import { Button, DocumentQRCode, PrintStyles } from '@components/common'
import { useDocumentRegistry } from '@hooks/useDocumentRegistry'
import { toast } from 'react-toastify'
import api from '@services/api'
import { useAuth } from '@context/AuthContext'

const STATUS_COLORS = {
  requested: 'warning',
  approved: 'info',
  rejected: 'error',
  in_use: 'primary',
  returned: 'success',
  cancelled: 'default',
}

// Helper function to get initials from a name
const getInitials = (name) => {
  if (!name) return ''
  return name
    .split(' ')
    .map(part => part.charAt(0).toUpperCase())
    .join('')
}

const VehicleRequestPrint = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [request, setRequest] = useState(null)
  const [loading, setLoading] = useState(true)
  const { registerDocument, verificationCode, loading: registering } = useDocumentRegistry()

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const response = await api.get(`/assets/vehicle-requests/${id}/`)
        setRequest(response.data)
      } catch (error) {
        console.error('Error fetching vehicle request:', error)
        toast.error('Failed to load vehicle request')
        navigate('/assets/vehicle-requests')
      } finally {
        setLoading(false)
      }
    }
    fetchRequest()
  }, [id, navigate])

  // Set document title for meaningful PDF filename
  useEffect(() => {
    if (request) {
      document.title = `Vehicle Request - ${request.request_number} - UCI CMMS`
    }
    return () => {
      document.title = 'UCI CMMS'
    }
  }, [request])

  // Register document when request is loaded
  useEffect(() => {
    if (request && !verificationCode) {
      registerDocument('vehicle_request', request.request_number, parseInt(id), request)
        .catch((err) => console.error('Failed to register document:', err))
    }
  }, [request, id, verificationCode, registerDocument])

  const handlePrint = async () => {
    if (!verificationCode && request) {
      try {
        await registerDocument('vehicle_request', request.request_number, parseInt(id), request)
      } catch (err) {
        console.error('Registration failed:', err)
      }
    }
    window.print()
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!request) {
    return null
  }

  return (
    <>
      {/* Print Controls - Hidden when printing */}
      <Box className="print-controls no-print" sx={{ p: 2, display: 'flex', gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          variant="outlined"
          onClick={() => navigate('/assets/vehicle-requests')}
        >
          Back
        </Button>
        <Button
          startIcon={<Print />}
          onClick={handlePrint}
          disabled={registering}
        >
          {registering ? 'Preparing...' : 'Print'}
        </Button>
      </Box>

      {/* Printable Content */}
      <Box className="print-content" sx={{ bgcolor: 'white' }}>
        {/* Header with UCI Logo */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          {/* Logo centered on first line */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <img
              src="/images/uci-logo.png"
              alt="UCI Logo"
              style={{ height: '70px' }}
            />
          </Box>
          {/* System name prominently displayed */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color: '#1B5E7A',
              letterSpacing: '0.5px',
              mb: 2
            }}
          >
            Computerized Maintenance Management System
          </Typography>
          {/* Document title */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', textDecoration: 'underline' }}>
            REQUEST FOR AUTHORIZATION/CLEARANCE TO USE UCI VEHICLE
          </Typography>
        </Box>

        {/* Notice */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            It is the instruction of all UCI designated officers/staff members that, no use of a UCI vehicle
            without prior authorization. No use of a UCI vehicle should start before this form is completed
            and duly approved. This information is for security purposes.
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 1 }}>
            This form must be submitted forty eight (48) hours in advance or sufficient lead time of the
            proposed time of departure from the duty station.
          </Typography>
        </Paper>

        {/* Request Number and Date */}
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '25%', bgcolor: 'grey.100' }}>Request Number</TableCell>
                <TableCell sx={{ width: '25%' }}>{request.request_number}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '25%', bgcolor: 'grey.100' }}>Date</TableCell>
                <TableCell sx={{ width: '25%' }}>
                  {request.requested_date ? formatDate(request.requested_date) : '-'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Status</TableCell>
                <TableCell colSpan={3}>
                  <Chip
                    label={request.status?.replace('_', ' ').toUpperCase()}
                    color={STATUS_COLORS[request.status] || 'default'}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Requesting Official Section */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, bgcolor: 'grey.100', p: 1 }}>
          REQUESTING OFFICIAL
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Name of Official(s)</TableCell>
                <TableCell>{request.requested_by_name || '_______________'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Rank/Position</TableCell>
                <TableCell>{request.requester_rank || request.requested_by_job_title || '_______________'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Section/Unit</TableCell>
                <TableCell>{request.department_name || request.requested_by_department_name || '_______________'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Signature</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ borderBottom: '1px solid black', minWidth: 100, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {request.requester_signature ? (
                        <Box
                          component="img"
                          src={request.requester_signature}
                          sx={{ maxHeight: 28, objectFit: 'contain' }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ fontStyle: 'italic', fontWeight: 'bold' }}>
                          {request.requested_by_initials || getInitials(request.requested_by_name)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Signature Date</TableCell>
                <TableCell>
                  {request.requester_signature_date
                    ? formatDate(request.requester_signature_date)
                    : formatDate(request.requested_date) || '_______________'}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Trip Details Section */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, bgcolor: 'grey.100', p: 1 }}>
          TRIP DETAILS
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Number of Staff Members on Board</TableCell>
                <TableCell>{request.num_staff_on_board || 1}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Number of Non-Staff Members on Board</TableCell>
                <TableCell>{request.num_non_staff_on_board || 0}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Day: Time of Departure</TableCell>
                <TableCell>
                  {request.departure_date
                    ? formatDateTime(request.departure_date)
                    : '_______________'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>From UCI to</TableCell>
                <TableCell>{request.destination || '_______________'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Day: Time of Return</TableCell>
                <TableCell>
                  {request.expected_return_date
                    ? formatDateTime(request.expected_return_date)
                    : '_______________'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Vehicle Reg. No.</TableCell>
                <TableCell>{request.vehicle_registration || '_______________'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Vehicle Name/Type</TableCell>
                <TableCell>{request.vehicle_name} {request.vehicle_type && `(${request.vehicle_type})`}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Purpose of Visit</TableCell>
                <TableCell>{request.purpose || '_______________'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 3 }} />

        {/* Approval Section */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, bgcolor: 'grey.100', p: 1 }}>
          APPROVAL BY THE EXECUTIVE DIRECTOR UCI/IN-CHARGE OF TRANSPORT FOR SECURITY
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Signature</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ borderBottom: '1px solid black', minWidth: 100, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {request.approver_signature ? (
                        <Box
                          component="img"
                          src={request.approver_signature}
                          sx={{ maxHeight: 28, objectFit: 'contain' }}
                        />
                      ) : request.approved_by_name ? (
                        <Typography variant="body2" sx={{ fontStyle: 'italic', fontWeight: 'bold' }}>
                          {request.approved_by_initials || getInitials(request.approved_by_name)}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                <TableCell>{request.approved_by_name || '_______________'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                <TableCell>{request.approver_title || request.approved_by_job_title || '_______________'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell>
                  {request.approved_date
                    ? formatDate(request.approved_date)
                    : '_______________'}
                </TableCell>
              </TableRow>
              {request.approval_notes && (
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                  <TableCell>{request.approval_notes}</TableCell>
                </TableRow>
              )}
              {request.rejection_reason && (
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', color: 'error.main' }}>Rejection Reason</TableCell>
                  <TableCell sx={{ color: 'error.main' }}>{request.rejection_reason}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Trip Execution Details (if trip has started/completed) */}
        {request.actual_departure_date && (
          <>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, bgcolor: 'grey.100', p: 1 }}>
              TRIP EXECUTION RECORD
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Actual Departure</TableCell>
                    <TableCell>{formatDateTime(request.actual_departure_date)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Mileage Out</TableCell>
                    <TableCell>{request.mileage_out?.toLocaleString()} km</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Started By</TableCell>
                    <TableCell>{request.started_by_name || '-'}</TableCell>
                  </TableRow>
                  {request.actual_return_date && (
                    <>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Actual Return</TableCell>
                        <TableCell>{formatDateTime(request.actual_return_date)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Mileage Return</TableCell>
                        <TableCell>{request.mileage_return?.toLocaleString()} km</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Distance Traveled</TableCell>
                        <TableCell>{request.distance_traveled?.toLocaleString()} km</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Days Outside Compound</TableCell>
                        <TableCell>{request.days_outside_compound} days</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Condition on Return</TableCell>
                        <TableCell>
                          <Chip
                            label={request.condition_on_return_display || request.condition_on_return}
                            color={request.condition_on_return === 'good' ? 'success' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      {request.faults_encountered && (
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', color: 'error.main' }}>Faults Encountered</TableCell>
                          <TableCell sx={{ color: 'error.main' }}>{request.faults_encountered}</TableCell>
                        </TableRow>
                      )}
                      {request.return_notes && (
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Return Notes</TableCell>
                          <TableCell>{request.return_notes}</TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Returned By</TableCell>
                        <TableCell>{request.returned_by_name || '-'}</TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Printed: {formatDateTime(new Date())}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Printed by: {(user?.first_name || user?.last_name) ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim() : user?.username || 'N/A'}
              </Typography>
            </Box>
            <DocumentQRCode
              verificationCode={verificationCode}
              documentType="Vehicle Request"
              documentNumber={request?.request_number || ''}
              size={80}
            />
          </Box>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
            RESEARCH IS OUR RESOURCE
          </Typography>
        </Box>
      </Box>

      <PrintStyles orientation="portrait" />
    </>
  )
}

export default VehicleRequestPrint
